// In this file, if an error occurs an object is thrown which is caught and
// then returned from the component, so we will allow non-Errors to be thrown
// here.
/* eslint-disable no-throw-literal */
import queryString from 'querystring';
import { isEmpty, intersection, pick, pickBy, merge, map, uniq } from 'lodash';
import getEnvValue from 'commons/getEnvValue';

const API_KEY =
  getEnvValue('NEXT_PUBLIC_SMARTY_STREET_EMBED_KEY') ??
  getEnvValue('REACT_APP_SMARTY_STREET_EMBED_KEY') ??
  '';

const US_AUTOCOMPLETE_PRO_API_URL =
  'https://us-autocomplete-pro.api.smartystreets.com/lookup';
const STREET_ADDRESS_VALIDATION_API_URL =
  'https://us-street.api.smartystreets.com/street-address';
// 6055 E washington blvd
const filterDuplicateSuggestions = allSuggestions =>
  map(
    uniq(
      map(allSuggestions, obj => {
        delete obj.secondary;
        delete obj.entries;
        return JSON.stringify(obj);
      })
    ),
    obj => JSON.parse(obj)
  ) || [];

export const USAutocompleteProApi = search =>
  new Promise((resolve, reject) => {
    if (search.length < 5) {
      reject(new Error('Search must be at least 5 characters long'));
    }
    const requestUrl = `${US_AUTOCOMPLETE_PRO_API_URL}?${queryString.stringify({
      key: API_KEY,
      search,
    })}`;
    fetch(requestUrl, {
      headers: {
        referer: 'https://joybird.com',
      },
      method: 'GET',
    })
      .then(res => res.json())
      .then(data => {
        const uniqSuggestions = filterDuplicateSuggestions(data?.suggestions);
        resolve(uniqSuggestions);
      })
      .catch(() => {
        reject(new Error('Error fetching suggestions'));
      });
  });
const renameProps = (keysMap, obj) =>
  Object.keys(obj).reduce(
    (acc, key) => ({
      ...acc,
      ...{ [keysMap[key] || key]: obj[key] },
    }),
    {}
  );

export const streetAddressValidationApi = async values => {
  try {
    const defaults = {
      match: 'invalid',
      candidates: 10,
    };

    const DPV_MATCH_CODE = {
      Y: 'Confirmed',
      N: 'The Address is not valid',
      S:
        'The main address is valid, but the submitted secondary information (apartment, suite, etc.) was not recognized',
      D:
        'The main address is valid, but it is missing secondary information (apartment, suite, etc.).',
    };
    const DPV_FOOTNOTES = {
      A1: 'Address is invalid.',
      CC:
        'The submitted secondary information (apartment, suite, etc.) was not recognized',
      M1: 'In the address the primary number (e.g., house number) is missing.',
      M3: 'Primary number (e.g., house number) is invalid.',
      N1:
        'In the address the primary number Address is missing secondary information (apartment, suite, etc.).',
      P1: 'In the address the PO, RR, or HC box number is missing.',
      P3: 'In the address the PO, RR, or HC box number is invalid.',
      R1: 'The address is without private mailbox (PMB) info.',
    };

    let params = pick(values, [
      'address_line1',
      'address_line2',
      'city',
      'state',
      'zip_code',
      'match',
      'candidates',
    ]);
    params = pickBy(
      renameProps(
        {
          address_line1: 'street',
          address_line2: 'secondary',
          zip_code: 'zipcode',
        },
        params
      ),
      value => !(!value || value === '')
    );
    if (isEmpty(params)) {
      throw new Error('Address parameters are required!');
    }
    params = merge(defaults, params);
    const requestUrl = `${STREET_ADDRESS_VALIDATION_API_URL}?${queryString.stringify(
      {
        key: API_KEY,
        ...params,
      }
    )}`;

    const response = await fetch(requestUrl, {
      headers: {
        referer: 'https://joybird.com',
      },
      method: 'GET',
    });

    if (response?.status === 200) {
      const data = await response.json();
      if (isEmpty(data) || isEmpty(data[0])) {
        return null;
      }

      const dpvMatchCode = data[0].analysis?.dpv_match_code;
      const dpvFootnotes = intersection(
        ['A1', 'CC', 'M1', 'M3', 'P1', 'P3', 'R1'],
        data[0].analysis?.dpv_footnotes.match(/.{1,2}/g) || []
      );

      if (dpvMatchCode && dpvMatchCode === 'N') {
        throw {
          code: dpvMatchCode,
          message: DPV_MATCH_CODE[dpvMatchCode],
          type: 'dpv_match_code',
        };
      }

      if (!isEmpty(dpvFootnotes)) {
        const message = dpvFootnotes.map(dfn => DPV_FOOTNOTES[dfn]).join(' ');
        throw {
          code: dpvFootnotes,
          message: message.trim(),
          type: 'dpv_footnotes',
        };
      }
      if (data[0].analysis?.dpv_vacant !== 'N') {
        throw {
          code: data[0].analysis.dpv_vacant,
          message:
            'Address is not active and is not receiving deliveries over 90 days',
          type: 'dpv_vacant',
        };
      }
      return { suggestion: { ...data[0].components } };
    }
  } catch (e) {
    // console.log('>>>>>>> Error', e);
    return e;
  }
  return null;
};
